import React, { useState } from "react";
import { pollsApi } from "../../../services/api/pollsApi";
import { FaPlus, FaTrash } from "react-icons/fa";
import "./PostForms.css";

const PollPostForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    question: "",
    options: ["", ""],
    allowMultiple: false,
    expiresAt: "",
    content: "",
    visibility: "public",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleAddOption = () => {
    if (formData.options.length < 10) {
      setFormData({
        ...formData,
        options: [...formData.options, ""],
      });
    }
  };

  const handleRemoveOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData({ ...formData, options: newOptions });
    }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.question.trim()) {
      setError("Please enter a poll question");
      return;
    }

    const validOptions = formData.options.filter((opt) => opt.trim());
    if (validOptions.length < 2) {
      setError("Please provide at least 2 options");
      return;
    }

    setSubmitting(true);

    try {
      const pollData = {
        poll: {
          question: formData.question,
          options: validOptions, // Backend will transform to { text, votes } format
          allowMultiple: formData.allowMultiple,
          expiresAt: formData.expiresAt || null,
        },
        content: formData.content,
        visibility: formData.visibility,
      };

      const response = await pollsApi.createPoll(pollData);

      const createdPost =
        response.data.post || response.data.data || response.data;

      onSuccess(createdPost);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create poll");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="post-form poll-form">
      {error && <div className="error-message">{error}</div>}

      {/* Poll Question */}
      <div className="form-group">
        <label htmlFor="question">Poll Question *</label>
        <input
          type="text"
          id="question"
          value={formData.question}
          onChange={(e) =>
            setFormData({ ...formData, question: e.target.value })
          }
          placeholder="What would you like to ask?"
          maxLength={200}
          required
        />
      </div>

      {/* Poll Options */}
      <div className="form-group">
        <label>Options * (2-10)</label>
        <div className="poll-options-list">
          {formData.options.map((option, index) => (
            <div key={index} className="poll-option-input">
              <input
                type="text"
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
                placeholder={`Option ${index + 1}`}
                maxLength={100}
              />
              {formData.options.length > 2 && (
                <button
                  type="button"
                  onClick={() => handleRemoveOption(index)}
                  className="remove-option-btn"
                  title="Remove option"
                >
                  <FaTrash />
                </button>
              )}
            </div>
          ))}
        </div>
        {formData.options.length < 10 && (
          <button
            type="button"
            onClick={handleAddOption}
            className="add-option-btn"
          >
            <FaPlus /> Add Option
          </button>
        )}
      </div>

      {/* Allow Multiple Choice */}
      <div className="form-group checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={formData.allowMultiple}
            onChange={(e) =>
              setFormData({ ...formData, allowMultiple: e.target.checked })
            }
          />
          <span>Allow multiple choices</span>
        </label>
      </div>

      {/* Expiration Date */}
      <div className="form-group">
        <label htmlFor="expiresAt">Expiration Date (Optional)</label>
        <input
          type="datetime-local"
          id="expiresAt"
          value={formData.expiresAt}
          onChange={(e) =>
            setFormData({ ...formData, expiresAt: e.target.value })
          }
          min={new Date().toISOString().slice(0, 16)}
        />
      </div>

      {/* Additional Content */}
      <div className="form-group">
        <label htmlFor="content">Additional Context (Optional)</label>
        <textarea
          id="content"
          value={formData.content}
          onChange={(e) =>
            setFormData({ ...formData, content: e.target.value })
          }
          placeholder="Add any additional context or details..."
          rows={3}
          maxLength={1000}
        />
      </div>

      {/* Visibility */}
      <div className="form-group">
        <label htmlFor="visibility">Visibility</label>
        <select
          id="visibility"
          value={formData.visibility}
          onChange={(e) =>
            setFormData({ ...formData, visibility: e.target.value })
          }
        >
          <option value="public">Public</option>
          <option value="connections">Connections Only</option>
          <option value="private">Private</option>
        </select>
      </div>

      {/* Actions */}
      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-btn">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="submit-btn">
          {submitting ? "Creating..." : "Create Poll"}
        </button>
      </div>
    </form>
  );
};

export default PollPostForm;
